import { snakeCase } from 'lodash-es';
import {
  STUDY_AUTO_DOCUMENT_EXTRACTIONS,
  STUDY_AUTO_DOCUMENT_FIELD_EXTRACTION,
  tokenizeRoute,
} from '../routes';

export type InterventionType = 'control' | 'intervention';

// A field carries one suggestion. Where the extractor found several values,
// the server combines them into a single suggestion string.
export type AutoDocumentExtraction = {
  suggestion: string | null;
  quotes: string[];
};

// intervention_group is the exception: one suggestion per intervention arm.
export type InterventionGroupSuggestion = AutoDocumentExtraction & {
  description?: string | null;
  type?: InterventionType | null;
};

export type AutoDocumentExtractionsByField = Record<
  string,
  AutoDocumentExtraction
>;

type FieldRequestParams = {
  fieldName: string;
  documentId: number;
  studyId: number;
};

export type GetAutoDocumentExtractionParams = FieldRequestParams;

export type GetAllAutoDocumentExtractionsParams = {
  documentId: number;
  studyId: number;
};

const requestJSON = (url: URL) =>
  fetch(url.toString(), {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => res.json());

const fieldURL = ({ fieldName, documentId, studyId }: FieldRequestParams) =>
  tokenizeRoute(STUDY_AUTO_DOCUMENT_FIELD_EXTRACTION, {
    studyId,
    documentId,
    fieldName: snakeCase(fieldName),
  });

// Every field's suggestion for a document, keyed by field name. Unlike the
// per-field request this does not mark suggestions as surfaced, so it is safe
// to ask about fields the reviewer has never looked at. intervention_group is
// not included.
export const getAllAutoDocumentExtractions = async ({
  documentId,
  studyId,
}: GetAllAutoDocumentExtractionsParams): Promise<AutoDocumentExtractionsByField | null> =>
  requestJSON(
    tokenizeRoute(STUDY_AUTO_DOCUMENT_EXTRACTIONS, {
      studyId,
      documentId,
    })
  );

export const getInterventionGroupSuggestions = async (
  params: Omit<FieldRequestParams, 'fieldName'>
): Promise<InterventionGroupSuggestion[]> =>
  requestJSON(fieldURL({ ...params, fieldName: 'intervention_group' }));
